/**
 * What a drawn stroke does to the board.
 *
 * A swim is not only an annotation: the player it starts on travels to the end
 * of the line, and takes the ball with them when they have it. Who has the ball
 * is one rule, used everywhere: **the ball goes with the nearest player within
 * reach**, and on a tie, with the one who had it last. So a defender swimming
 * right beside the attacker never takes the ball, and a coach shows a steal by
 * putting the ball on the defender. The rules here are generic — the pack says
 * which entity type is carried and how close it has to be, and this module only
 * ever compares ids and distances.
 *
 * The stroke is a lane into the phase it is drawn on: the move edits that phase
 * in place, exactly as dragging a player does, so the coach sees the piece land
 * where the lane ends. Playback then swims it there from the phase before (see
 * `lanes.ts`).
 */

import { clampToField, equals, type Vec2 } from './geometry.ts';
import { FIELD_INSET, type PlayDocument } from './document.ts';
import type { Id } from './ids.ts';
import type { PoseMap } from './timeline.ts';
import type { SportPack } from './sport.ts';

function distance(a: Vec2, b: Vec2): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * The entities a stroke from `moverId` takes with it, the mover first.
 *
 * `withBall` is the tool's decision — a swim off the ball carries nothing — and
 * the radius is the pack's.
 */
export function carriedEntities(
  doc: PlayDocument,
  pack: SportPack,
  poses: PoseMap,
  moverId: Id,
  withBall: boolean,
  lastHolder?: Id,
): readonly Id[] {
  const rule = pack.possession;
  if (!withBall || !rule) return [moverId];

  const from = poses[moverId];
  if (!from) return [moverId];

  // Only a ball this player has: within reach is not enough when someone else
  // is nearer to it.
  const carried = doc.entities
    .filter((entity) => entity.typeId === rule.carriedTypeId && entity.id !== moverId)
    .filter((entity) => holderOf(doc, pack, poses, entity.id, lastHolder) === moverId)
    .map((entity) => entity.id);

  return [moverId, ...carried];
}

/**
 * The piece holding `ballId`: the nearest non-ball piece within reach of it.
 * Nothing when `ballId` is not the carried type or nobody is close enough.
 *
 * A stroke that starts on the ball rather than on its holder still means the
 * holder: a coach dribbling from the ball expects the player to come too.
 */
export function holderOf(
  doc: PlayDocument,
  pack: SportPack,
  poses: PoseMap,
  ballId: Id,
  lastHolder?: Id,
): Id | undefined {
  const rule = pack.possession;
  const ball = doc.entities.find((entity) => entity.id === ballId);
  const at = poses[ballId];
  if (!rule || !at || ball?.typeId !== rule.carriedTypeId) return undefined;

  const inReach = doc.entities
    .filter((entity) => entity.typeId !== rule.carriedTypeId)
    .map((entity) => ({ id: entity.id, distance: poses[entity.id] ? distance(poses[entity.id]!, at) : Infinity }))
    .filter((candidate) => candidate.distance <= rule.radius)
    .sort((a, b) => a.distance - b.distance);
  const nearest = inReach[0];
  if (!nearest) return undefined;
  // Equally near: the ball stays with whoever had it last.
  const tied = inReach.filter((candidate) => candidate.distance - nearest.distance <= TIE);
  return tied.some((candidate) => candidate.id === lastHolder) ? lastHolder : nearest.id;
}

/**
 * Who had the ball before the phase being drafted: its holder in the phase
 * before `keyframeId`. That is "the player who had it last", which breaks a
 * tie when two players are equally near the ball. Nobody on the first phase.
 */
export function lastHolder(doc: PlayDocument, pack: SportPack, keyframeId: Id): Id | undefined {
  const rule = pack.possession;
  const previous = doc.keyframes[doc.keyframes.findIndex((k) => k.id === keyframeId) - 1];
  const ball = rule ? doc.entities.find((entity) => entity.typeId === rule.carriedTypeId) : undefined;
  if (!previous || !ball) return undefined;
  return holderOf(doc, pack, previous.positions, ball.id);
}

/** Distances closer than this (in field units) are a tie — far below anything a coach can place by hand. */
const TIE = 1e-6;

/**
 * What a stroke from `holderId` sends away — the ball of a pass or a shot.
 *
 * A stroke begun on the carried entity itself releases that entity, so a coach
 * can throw from the ball as readily as from the player beside it. When two are
 * in reach the nearer one goes, which keeps the choice predictable.
 *
 * Returns nothing when there is nothing to throw: no possession rule, no pose,
 * or a player who is not near the ball. Such a stroke stays an annotation.
 */
export function heldEntity(
  doc: PlayDocument,
  pack: SportPack,
  poses: PoseMap,
  holderId: Id,
  lastHolder?: Id,
): Id | undefined {
  const rule = pack.possession;
  if (!rule) return undefined;

  const from = poses[holderId];
  if (!from) return undefined;

  const holder = doc.entities.find((entity) => entity.id === holderId);
  if (holder?.typeId === rule.carriedTypeId) return holder.id;

  // The ball this player has — not one they are merely near while someone
  // else is nearer, so a defender can't pass the attacker's ball.
  return doc.entities
    .filter((entity) => entity.typeId === rule.carriedTypeId)
    .map((entity) => ({ id: entity.id, at: poses[entity.id] }))
    .filter((candidate) => candidate.at !== undefined && holderOf(doc, pack, poses, candidate.id, lastHolder) === holderId)
    .sort((a, b) => distance(a.at!, from) - distance(b.at!, from))[0]?.id;
}

/**
 * Puts the mover at `to`, bringing everything carried along with it.
 *
 * Carried entities keep the offset they started with, so the ball stays beside
 * the player rather than snapping underneath. Everything lands inside the
 * field, and entities not named are returned untouched.
 *
 * This is the whole geometry of a carry, shared deliberately: the board draws
 * the stroke in progress through it and `relocateEntities` commits through it,
 * so what the coach sees while drawing is what they get on release.
 */
export function carryPoses(
  poses: PoseMap,
  moverId: Id,
  carriedIds: readonly Id[],
  to: Vec2,
): PoseMap {
  const from = poses[moverId];
  if (!from) return poses;

  const landed = clampToField(to, FIELD_INSET);
  const next: Record<Id, Vec2> = { ...poses, [moverId]: landed };

  for (const id of carriedIds) {
    const carriedFrom = poses[id];
    if (!carriedFrom) continue;
    next[id] = clampToField(
      { x: landed.x + (carriedFrom.x - from.x), y: landed.y + (carriedFrom.y - from.y) },
      FIELD_INSET,
    );
  }

  return next;
}

/**
 * Commits a lane drawn on `keyframeId`: the mover, and everything carried, land
 * at `to` in that phase. No phase is added.
 *
 * The landing carries on through the phases after while the piece held still
 * there. Otherwise a later phase copied before the lane was drawn would swim it
 * straight back to where it started.
 */
export function relocateEntities(
  doc: PlayDocument,
  now: number,
  options: {
    readonly keyframeId: Id;
    readonly moverId: Id;
    readonly carriedIds: readonly Id[];
    readonly to: Vec2;
  },
): PlayDocument {
  const index = doc.keyframes.findIndex((k) => k.id === options.keyframeId);
  if (index < 0) return doc;

  const frame = doc.keyframes[index]!;
  const landed = carryPoses(frame.positions, options.moverId, options.carriedIds, options.to);
  const keyframes = [...doc.keyframes];
  keyframes[index] = { ...frame, positions: landed };

  for (const id of [options.moverId, ...options.carriedIds]) {
    const target = landed[id];
    const held = frame.positions[id];
    if (!target || !held) continue;
    for (let j = index + 1; j < keyframes.length; j += 1) {
      const later = keyframes[j]!;
      const current = later.positions[id];
      if (!current || !equals(current, held)) break;
      keyframes[j] = { ...later, positions: { ...later.positions, [id]: target } };
    }
  }

  return { ...doc, keyframes, updatedAt: now };
}
